import { useMemo, useCallback } from "react"
import { useAppContext } from "@components/AppContext"
import { getUsDateFromIsoDate } from "@components/Date"
import { createBatchInvestorFilesOperation as createOperation } from "@api/services/backstage"


const useTargetBatches = ({
  targetProjectId,
  filesToMove,
  filesToOverride,
  sourceBatches,
  targetBatches,
  setTargetBatches,
}) => {
  const { request } = useAppContext()

  const sourceBatchIds = useMemo(() =>
    [ ...filesToMove, ...filesToOverride ].map(({ batchId: sourceBatchId }) => sourceBatchId)
  , [ filesToMove, filesToOverride ])

  const getSourceBatch = useCallback(sourceBatchId =>
    sourceBatches.find(({ id }) => id === sourceBatchId)
  , [ sourceBatches ])

  const getTargetBatchName = useCallback(sourceBatchId => {
    const { name } = getSourceBatch(sourceBatchId)
    const usDate = getUsDateFromIsoDate(new Date())

    const targetBatchName = `${name} (Transferred on ${usDate})`
    return targetBatchName
  }, [
    getSourceBatch
  ])

  const createTargetBatch = useCallback(async (sourceBatchId) => {
    const name = getTargetBatchName(sourceBatchId)

    const {
      date,
      documentType
    } = getSourceBatch(sourceBatchId)

    const isSent = true

    const projectId = targetProjectId
    const scheduledAt = undefined
    const investorFiles = []

    const mutation = {
      name,
      date,
      isSent,
      projectId,
      scheduledAt,
      documentType,
      investorFiles,
    }

    const { data: newBatch } = await request(createOperation, { mutation })

    return newBatch
  }, [
    request,
    getSourceBatch,
    targetProjectId,
    getTargetBatchName,
  ]);

  const ensureTargetBatches = useCallback(async () => {
    const allTargetBatches = [ ...targetBatches ]

    const getTargetBatch = (sourceBatchId) => {
      const targetBatchName = getTargetBatchName(sourceBatchId)

      const targetBatch = allTargetBatches
        .find(({ name }) => name === targetBatchName)

      return targetBatch
    }

    for (const sourceBatchId of sourceBatchIds) {
      const isTargetBatchExist = !!getTargetBatch(sourceBatchId)

      if (!isTargetBatchExist) {
        const createdBatch = await createTargetBatch(sourceBatchId)
        allTargetBatches.push(createdBatch)
      }
    }

    const getTargetBatchId = (sourceBatchId) => {
      const targetBatch = getTargetBatch(sourceBatchId)
      return targetBatch.id
    }

    setTargetBatches(allTargetBatches)

    return {
      getTargetBatchId,
    }
  }, [
    targetBatches,
    sourceBatchIds,
    setTargetBatches,
    createTargetBatch,
    getTargetBatchName,
  ])

  return {
    ensureTargetBatches,
  }
}

export default useTargetBatches
